import type {
  RecentStorageChange,
  StorageTraceRef,
} from "../../api/admin-content";
import { tableStyles as ts } from "../../components/TableParts";
import { t } from "../../i18n";
import {
  formatBytes,
  formatDate,
  shortId,
  sourceTypeLabel,
  storageStatusLabel,
} from "./storage-detail-formatters";

export function StorageTraceTable({
  changes,
}: {
  changes: ReadonlyArray<RecentStorageChange>;
}): React.JSX.Element {
  if (changes.length === 0) {
    return <p style={ts.emptyText}>{t("storage_detail.no_recent_changes")}</p>;
  }
  return (
    <table style={ts.table}>
      <thead>
        <tr>
          <th style={ts.th}>{t("storage_detail.col_object")}</th>
          <th style={ts.th}>{t("storage_detail.col_size")}</th>
          <th style={ts.th}>{t("storage_detail.col_state")}</th>
          <th style={ts.th}>{t("storage_detail.col_trace")}</th>
          <th style={ts.th}>{t("storage_detail.col_created")}</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((change) => (
          <TraceRow key={change.id} change={change} />
        ))}
      </tbody>
    </table>
  );
}

function TraceRow({
  change,
}: {
  change: RecentStorageChange;
}): React.JSX.Element {
  return (
    <tr style={ts.row}>
      <td style={ts.td}>
        <div>{change.object_type}</div>
        <div style={styles.mono}>{shortId(change.id)}</div>
      </td>
      <td style={ts.td}>{formatBytes(change.size_bytes)}</td>
      <td style={ts.td}>
        <div>{storageStatusLabel(change.status)}</div>
        <div style={styles.subText}>{change.bucket_binding}</div>
      </td>
      <td style={ts.td}>
        <TraceCell change={change} />
      </td>
      <td style={ts.td}>
        <div>{formatDate(change.created_at)}</div>
        {change.deleted_at && (
          <div style={styles.subText}>
            {t("storage_detail.deleted_at", {
              date: formatDate(change.deleted_at),
            })}
          </div>
        )}
      </td>
    </tr>
  );
}

function TraceCell({
  change,
}: {
  change: RecentStorageChange;
}): React.JSX.Element {
  if (change.trace_status === "unresolved") {
    return (
      <div>
        <span style={{ ...ts.badge, ...styles.unresolvedBadge }}>
          {t("storage_detail.trace_unresolved")}
        </span>
        <TraceMeta
          label={t("storage_detail.storage_object_id")}
          id={change.id}
        />
        {change.conversation_id && (
          <TraceMeta
            label={t("storage_detail.conversation_id")}
            id={change.conversation_id}
          />
        )}
      </div>
    );
  }
  return (
    <div style={styles.traceRefs}>
      {change.trace_refs.map((ref) => (
        <TraceRefRow key={`${ref.ref_type}:${ref.ref_id}`} refItem={ref} />
      ))}
    </div>
  );
}

function TraceRefRow({
  refItem,
}: {
  refItem: StorageTraceRef;
}): React.JSX.Element {
  return (
    <div style={styles.traceRef}>
      <span style={{ ...ts.badge, ...styles.resolvedBadge }}>
        {sourceTypeLabel(refItem.ref_type)}
      </span>
      <TraceMeta label={t("storage_detail.ref_id")} id={refItem.ref_id} />
      {refItem.message_id && (
        <TraceMeta
          label={t("storage_detail.message_id")}
          id={refItem.message_id}
        />
      )}
      {refItem.upload_id && (
        <TraceMeta
          label={t("storage_detail.upload_id")}
          id={refItem.upload_id}
        />
      )}
      {refItem.conversation_id && (
        <TraceMeta
          label={t("storage_detail.conversation_id")}
          id={refItem.conversation_id}
        />
      )}
    </div>
  );
}

function TraceMeta({
  label,
  id,
}: {
  label: string;
  id: string;
}): React.JSX.Element {
  return (
    <span style={styles.traceMeta} title={id}>
      {label}: <span style={styles.mono}>{shortId(id)}</span>
    </span>
  );
}

const styles: Record<string, React.CSSProperties> = {
  traceRefs: { display: "grid", gap: 6 },
  traceRef: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 },
  traceMeta: {
    display: "inline-flex",
    gap: 4,
    color: "var(--color-text-secondary)",
    fontSize: 12,
  },
  mono: { fontFamily: "monospace", fontSize: 12 },
  subText: {
    marginTop: 4,
    color: "var(--color-text-secondary)",
    fontSize: 12,
  },
  resolvedBadge: { color: "var(--color-success)" },
  unresolvedBadge: { color: "var(--color-danger)" },
};
